import { useCallback } from 'react';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { useGetQuickFilterValue, useListingLibQuickFilters } from 'framework-listing-lib/hooks/useQuickFilters';
import { IN, EQ } from 'framework-listing-lib/utils/filters';
import ListingLibFilters from 'framework-listing-lib/filters/ListingLibFilters';
import { CONTENT_PLATFORM_PROPERTY_NAME, useAuthInfoProvider } from 'content-management-data-lib';
import QuickFilterSelectComponent from './QuickFilterSelectComponent';
import useFilterEditorProps from '../hooks/useFilterEditorProps';
import useContentPlatformPropertiesConfiguration from '../hooks/useContentPlatformPropertiesConfiguration';
import { hasCrmAccessScopes } from '../utils/authUtils';
import styled from 'styled-components';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ListingLibFiltersBar = Loadable({
  loader: () => import( /* webpackChunkName: "filter-editor" */'framework-listing-lib/filters/ListingLibFiltersBar').then(mod => mod.ListingLibFiltersBar),
  LoadingComponent: () => null
});
export const FilterDropdowns = ({
  customQuickFilterComponentsByProperty = {},
  quickFilters,
  removeQuickFilter,
  setQuickFilter,
  getQuickFilterValue
}) => {
  const {
    propertyDefinitionMap,
    quickFilterPropertyNames
  } = useContentPlatformPropertiesConfiguration();
  const onFilterChange = useCallback((propertyName, value) => {
    if (!value) {
      removeQuickFilter(propertyName);
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        removeQuickFilter(propertyName);
      } else {
        setQuickFilter(IN(propertyName, value));
      }
    } else if (propertyName === CONTENT_PLATFORM_PROPERTY_NAME.DOMAIN) {
      setQuickFilter(EQ(propertyName, value));
    }

    // TODO: tracking to be handled by apps or add library level tracking here
    // trackFilterInteraction(propertyName);
  }, [removeQuickFilter, setQuickFilter]);
  const renderedQuickFilters = quickFilterPropertyNames.reduce((acc, name, index) => {
    if (propertyDefinitionMap[name]) {
      const FilterComponent = customQuickFilterComponentsByProperty[name] || QuickFilterSelectComponent;
      acc.push( /*#__PURE__*/_jsx(FilterComponent, {
        propertyDefinition: {
          name,
          label: propertyDefinitionMap[name].label,
          options: propertyDefinitionMap[name].options
        },
        onFilterChange: onFilterChange,
        quickFilters: quickFilters,
        getQuickFilterValue: getQuickFilterValue,
        referencedObjectType: propertyDefinitionMap[name].referencedObjectType
      }, index));
    }
    return acc;
  }, []);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: renderedQuickFilters
  });
};
const FilterContainer = styled.div.withConfig({
  displayName: "QuickFilters__FilterContainer",
  componentId: "sc-1potu1d-0"
})(["display:inline-flex;gap:10px;flex-direction:row;justify-content:start;"]);
const QuickFilters = ({
  customQuickFilterComponentsByProperty
}) => {
  const {
    userInfo
  } = useAuthInfoProvider();
  const {
    scopes
  } = userInfo.user;
  const hasCrmAccess = hasCrmAccessScopes(scopes);
  const filterEditorProps = useFilterEditorProps();
  const {
    quickFilterPropertyNames
  } = useContentPlatformPropertiesConfiguration();
  const {
    quickFilters,
    removeQuickFilter,
    setQuickFilter
  } = useListingLibQuickFilters();
  const getQuickFilterValue = useGetQuickFilterValue();
  const listingLibFilterBarProps = {
    externalQuickFilterPropertyNames: quickFilterPropertyNames,
    isAndGroup: true,
    disableCustomQuickFilters: true,
    hideQuickFilterSummaryInEditor: true,
    filterEditorUiMessages: {
      viewerEmptyFilters: I18n.text('content-management-listing-lib.filterEditor.emptyStateMessage')
    }
  };
  return /*#__PURE__*/_jsx(ListingLibFilters, {
    filterComponent: /*#__PURE__*/_jsxs(FilterContainer, {
      "data-test-id": "filter-dropdowns",
      children: [/*#__PURE__*/_jsx(FilterDropdowns, {
        customQuickFilterComponentsByProperty: customQuickFilterComponentsByProperty,
        quickFilters: quickFilters,
        removeQuickFilter: removeQuickFilter,
        setQuickFilter: setQuickFilter,
        getQuickFilterValue: getQuickFilterValue
      }), hasCrmAccess && /*#__PURE__*/_jsx(ListingLibFiltersBar, Object.assign({}, filterEditorProps, listingLibFilterBarProps))]
    })
  });
};
export default QuickFilters;